import { HttpEvent, HttpEventType } from '@angular/common/http';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormArray, FormControl, FormGroup, UntypedFormArray } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LandAreaFormGroup } from 'src/app/components/land-areas/form-group';
import { createParcelIdentificationFormGroup } from 'src/app/components/parcel-identification/form-group';
import { AssetClassLandAreaModel } from 'src/app/core/asset_class';
import { AssetClassSizeModel } from 'src/app/core/comparable';
import { AssetClassBuilding } from 'src/app/core/comparable/_models/asset-class-building.model';
import { FileUploadService } from 'src/app/core/file-upload';
import { MeasurementStandardService } from 'src/app/core/v2/_services/measurement-standard.service';
import { PropertySubTypeService } from 'src/app/core/v2/_services/property-sub-type.service';
import { CoordinatePoint } from 'src/app/core/v3/coordinate-points';
import { LandArea, mapAssetClassLandAreaComponentToLandArea, mapLandAreaToAssetClassLandAreaComponent } from 'src/app/core/v3/land-area';
import { AcGroundsComponent } from '../ac-grounds/ac-grounds.component';
import { BuildingInternalAspectsComponent } from '../building-internal-aspects/building-internal-aspects.component';
import { BuildingSectionComponent } from '../building-section/building-section.component';
import { ExternalAspectsComponent } from '../external-aspects/external-aspects.component';
import { ParcelIdentifactionSectionComponent } from '../parcel-identifaction-section/parcel-identifaction-section.component';
import { ServicesAndInfrastructuresComponent } from '../services-and-infrastructures/services-and-infrastructures.component';

@Component({
  selector: 'kt-building-form',
  templateUrl: './building-form.component.html',
  styleUrls: ['./building-form.component.scss'],
})
export class BuildingFormComponent implements OnInit, OnDestroy {
  @ViewChild('buildingAccommodationLayoutFileInput') 
  layoutFileInputRef: ElementRef<HTMLInputElement>
  @ViewChild('buildingUnitBreakdownFileInput')
  unitBreakdownFileInputRef: ElementRef<HTMLInputElement>

  private _unitOfAreaMeasurementId$ = new BehaviorSubject<number>(null)
  @Input() readonly: boolean = false;
  @Input() isComplexForm: boolean = false;
  @Input() acBuilding: AssetClassBuilding;

  @Input() centerLat: number;
  @Input() centerLng: number;
  @Input() buildingId: number = null;
  @Input() buildingPictures: any[] = [];
  @Input() buildingPicturePath: string = null;
  @Input() parent: 'Comparable' | 'Inspection' = 'Comparable'
  @Input() building$: Observable<any>
  @Input() 
  set unitOfAreaMeasurement(value: number) {
    this._unitOfAreaMeasurementId$.next(value)
  }
  @Output() centerChange: EventEmitter<any> = new EventEmitter();
  @Output() picChange: EventEmitter<{pictures: any[], picture: any}> = new EventEmitter();

  @ViewChild(BuildingSectionComponent, {static: false})
  buildingSectionComponent: BuildingSectionComponent;
  @ViewChild(ExternalAspectsComponent, {static: false})
  externalAspectComponent: ExternalAspectsComponent;
  @ViewChild(BuildingInternalAspectsComponent, {static: false})
  internalAspectComponent: BuildingInternalAspectsComponent;
  @ViewChild(AcGroundsComponent, {static: false})
  acGroundsComponent: AcGroundsComponent;
  @ViewChild(ServicesAndInfrastructuresComponent, {static: false})
  servicesAndInfraComponent: ServicesAndInfrastructuresComponent;

  unitOfAreaMeasurementId$ = this._unitOfAreaMeasurementId$.asObservable()
  errorFields = [];

  serviceAndInfraData;
  externalAspectData;
  internalAspectData;
  grounds;
  sizesSubject = new BehaviorSubject<AssetClassSizeModel[]>([]);
  hasParcelChecked = false;

  parcelIdentificationForm = createParcelIdentificationFormGroup({
    addInfoOnLand: false,
    boundaryType: 1,
    landParcelName: null,
    coordinateReferenceSystemId: null,
    landArea: {
      planningStatusId: null,
      qosDegradationId: null,
      envConsiderations: null,
      otherConsiderations: null,
      plotArea: null
    },
  })   
  private _points$ = new BehaviorSubject<CoordinatePoint[]>([])
  coordinatePoints$ = this._points$.asObservable()
  private _landAreas$ = new BehaviorSubject<LandArea[]>([])
  landAreas$ = this._landAreas$.asObservable()
  private _onDestroy$ = new Subject<void>()

  private _constructionCategoryIds: number[] = []
  constructionCategoryFormArray = new FormArray<FormGroup<{
    title: FormControl<string>,
    description: FormControl<string>
  }>>([])

  private _buildingAccommodationLayoutFile$ = new BehaviorSubject<{
    isUploaded: boolean,
    file: {
      name: string,
      display_name: string,
      path: string
    }
  }>({
    isUploaded: false,
    file: null
  })
  buildingAccommodationLayoutFile$ = this._buildingAccommodationLayoutFile$.asObservable()

  private _buildingUnitBreakdownFile$ = new BehaviorSubject<{
    isUploaded: boolean,
    file: {
      name: string,
      display_name: string,
      path: string
    }
  }>({
    isUploaded: false,
    file: null
  })
  buildingUnitBreakdownFile$ = this._buildingUnitBreakdownFile$.asObservable()

  floorAreaMeasurementCommentControl = new FormControl<string>(null)
  private _floorAreaMeasurementIds: number[] = []
  floorAreaMeasurementFormArray = new FormArray<FormGroup<{
    title: FormControl<string>,
    property_sub_type_id: FormControl<number>,
    measurement_standard_id: FormControl<number>,
    name: FormControl<string>,
    path: FormControl<string>,
    display_name: FormControl<string>
  }>>([])
  private _propertySubTypes$ = new BehaviorSubject<{id: number, name: string}[]>([])
  propertySubTypes$ = this._propertySubTypes$.asObservable()
  private _measurementStandards$ = new BehaviorSubject<{id: number, name: string, property_sub_type_id: number}[]>([])
  measurementStandards$ = this._measurementStandards$.asObservable()

  constructor(
    private fileUploadService: FileUploadService,
    private propertySubTypeService: PropertySubTypeService,
    private measurementStandardService: MeasurementStandardService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this._getPropertySubTypes()
    this._getMeasurementStandards()
    if (this.acBuilding == null) {
      this.acBuilding = new AssetClassBuilding();
    }
    this.serviceAndInfraData = this.acBuilding.serviceAndInfra;
    this.externalAspectData = this.acBuilding.externalAspectData;
    this.internalAspectData = this.acBuilding.internalAspectData;
    this.grounds = this.acBuilding.grounds;
    this.sizesSubject.next(this.acBuilding.sizes);

    this._points$.next(this.acBuilding.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(this.acBuilding.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: this.acBuilding.parcelIdentification.add_info_on_land,
      boundary_type: this.acBuilding.parcelIdentification.boundary_type_id,
      land_parcel_name: this.acBuilding.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: this.acBuilding.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: this.acBuilding.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: this.acBuilding.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: this.acBuilding.parcelIdentification.landArea.env_considerations,
        other_considerations: this.acBuilding.parcelIdentification.landArea.other_considerations
      }
    })

    this.parcelIdentificationForm.controls.add_info_on_land.valueChanges.pipe(
      takeUntil(this._onDestroy$)
    ).subscribe(value => {
      this.hasParcelChecked = value
    })

    this._setConstructionCategoryFormArray(this.acBuilding.constructionCategories || [])
    this._buildingAccommodationLayoutFile$.next(
      this.acBuilding.accommodationLayoutFile 
        ? {isUploaded: true, file: { 
          name: this.acBuilding.accommodationLayoutFile.name,
          display_name: normalizeName(this.acBuilding.accommodationLayoutFile.name),
          path: this.acBuilding.accommodationLayoutFile.path,
        }}
        : {isUploaded: false, file: null}
    )
    this._buildingUnitBreakdownFile$.next(
      this.acBuilding.unitBreakdownFile
        ? {isUploaded: true, file: { 
          name: this.acBuilding.unitBreakdownFile.name,
          display_name: normalizeName(this.acBuilding.unitBreakdownFile.name),
          path: this.acBuilding.unitBreakdownFile.path
        }}
        : {isUploaded: false, file: null}
    )
    this.floorAreaMeasurementCommentControl.patchValue(this.acBuilding.floorAreaMeasumentComment)
    this._setFloorAreaMeasurementFormArray(this.acBuilding.floorAreaMeasurements || [])
  }

  ngOnDestroy(): void {
    this._onDestroy$.next()
    this._onDestroy$.complete()
  }


  validate(): boolean {
    let errorFields = [];
    let hasError = false;
    if (this.buildingSectionComponent && this.buildingSectionComponent.validate()) {
      hasError = true;
      errorFields = [...errorFields, ...this.buildingSectionComponent.errorFields];
    }

    this.errorFields = errorFields;
    return hasError;
  }

  onCenterChange(event: any) {
    this.centerChange.emit(event);
  }
  onPicChange(event: any) {
    this.picChange.emit(event);
  }

  onBuildingChange(event) {
    const b = cloneDeep(event);
    this.serviceAndInfraData = {...b.serviceAndInfraData, id: null};
    this.externalAspectData = {...b.externalAspectData, id: null};
    this.internalAspectData = {...b.internalAspectData, id: null};
    this.grounds = {...b.grounds, id: null};
    this.sizesSubject.next(b.sizes.map(s => ({...s, id: null})));
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: !!b.parcelIdentification.add_info_on_land,
      boundary_type: b.parcelIdentification.boundary_type_id,
      land_parcel_name: b.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: b.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: b.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: b.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: b.parcelIdentification.landArea.env_considerations,
        other_considerations: b.parcelIdentification.landArea.other_considerations
      }
    })
    this._points$.next(b.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(b.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
  }

  onBuildingCleared() {
    const id = this.acBuilding.id;
    this.acBuilding = new AssetClassBuilding();
    this.acBuilding.id = id;
    this.serviceAndInfraData = this.acBuilding.serviceAndInfra;
    this.externalAspectData = this.acBuilding.externalAspectData;
    this.internalAspectData = this.acBuilding.internalAspectData;
    this.grounds = this.acBuilding.grounds;
    this._points$.next(this.acBuilding.parcelIdentification.points.map(p => ({
      latitude: Number(p.lat),
      longitude: Number(p.lng)
    })))
    this._landAreas$.next(this.acBuilding.parcelIdentification.landAreaComponents.map(item => mapAssetClassLandAreaComponentToLandArea(item)))
    this.parcelIdentificationForm.patchValue({
      add_info_on_land: this.acBuilding.parcelIdentification.add_info_on_land,
      boundary_type: this.acBuilding.parcelIdentification.boundary_type_id,
      land_parcel_name: this.acBuilding.parcelIdentification.land_parcel_name,
      coordinate_reference_system_id: this.acBuilding.parcelIdentification.coordinate_reference_system_id,
      land_area: {
        planning_status_id: this.acBuilding.parcelIdentification.landArea.planning_status_id,
        qos_degradation_id: this.acBuilding.parcelIdentification.landArea.qos_degradation_id,
        env_considerations: this.acBuilding.parcelIdentification.landArea.env_considerations,
        other_considerations: this.acBuilding.parcelIdentification.landArea.other_considerations
      }
    })
    this.sizesSubject.next(this.acBuilding.sizes);
  }

  onParcelChecked(val: boolean) {
    this.hasParcelChecked = val;
  }

  getData() {
    const _building = new AssetClassBuilding();

    _building.id = this.acBuilding.id;

    const landArea = new AssetClassLandAreaModel()
    const landAreaForm = this.parcelIdentificationForm.controls.land_area as LandAreaFormGroup
    landArea.clear()
    landArea.id = this.acBuilding.parcelIdentification.landArea.id
    landArea.planning_status_id = landAreaForm.controls.planning_status_id.value
    landArea.qos_degradation_id = landAreaForm.controls.qos_degradation_id.value
    landArea.env_considerations = landAreaForm.controls.env_considerations.value
    landArea.other_considerations = landAreaForm.controls.other_considerations.value
    _building.parcelIdentification.add_info_on_land = this.parcelIdentificationForm.controls.add_info_on_land.value
    _building.parcelIdentification.land_parcel_name = this.parcelIdentificationForm.controls.land_parcel_name.value
    _building.parcelIdentification.coordinate_reference_system_id = this.parcelIdentificationForm.controls.coordinate_reference_system_id.value
    _building.parcelIdentification.boundary_type_id = this.parcelIdentificationForm.controls.boundary_type.value
    _building.parcelIdentification.points = this._points$.value.map(item => ({lat: item.latitude, lng: item.longitude}));
    _building.parcelIdentification.landArea = landArea;
    _building.parcelIdentification.landAreaComponents = this._landAreas$.value.map(item => mapLandAreaToAssetClassLandAreaComponent(item)) 
    if (this.buildingSectionComponent) {
      const _data = this.buildingSectionComponent.getDataForComparable();
      _building.infoSection.building_id = _data.building_id;
      _building.infoSection.building_name = _data.building_name;
      _building.infoSection.completion_year = _data.completion_year;
      _building.infoSection.is_part_of_property = _data.is_part_of_property;
      _building.infoSection.info_about_property = _data.info_about_property;
      _building.infoSection.building_type = _data.building_type;
      _building.infoSection.building_type_comment = _data.building_type_comment;
      _building.infoSection.building_grade_id = _data.building_grade_id;
      _building.infoSection.energy_efficiency_grade_id = _data.energy_efficiency_grade_id;
      _building.infoSection.developer = _data.developer;
      _building.infoSection.anchor_tenant = _data.anchor_tenant;
      _building.infoSection.foundation_type_id = _data.foundation_type_id;
      _building.infoSection.building_description = _data.building_description;
      _building.infoSection.estimated_age = _data.estimated_age;
      _building.infoSection.estimated_remaining_lifespan_from = _data.estimated_remaining_lifespan_from
      _building.infoSection.estimated_remaining_lifespan_to = _data.estimated_remaining_lifespan_to
      _building.infoSection.makani_no = _data.makani_no
      _building.infoSection.municipality_number = _data.municipality_number
      _building.infoSection.dewa_no = _data.dewa_no
      _building.infoSection.height = _data.height
      _building.infoSection.plot_area = _data.plot_area
    }
    if (this.servicesAndInfraComponent) {
      _building.serviceAndInfra = this.servicesAndInfraComponent.getdata();
    }
    if (this.externalAspectComponent) {
      _building.externalAspectData = this.externalAspectComponent.getData();
    }

    if (this.internalAspectComponent) {
      _building.internalAspectData = this.internalAspectComponent.getData();
    }

    if (this.acGroundsComponent) {
      _building.grounds = {...this.acGroundsComponent.getData(), id: this.acBuilding.grounds.id};
    }
    _building.sizes = this.sizesSubject.value;
    _building.constructionCategories = this._getConstructionCategoryValue()
    const accommodationLayoutFile = this._buildingAccommodationLayoutFile$.getValue()
    _building.accommodationLayoutFile = accommodationLayoutFile.isUploaded 
      ? { name: accommodationLayoutFile.file.name, path: accommodationLayoutFile.file.path }
      : null
    const unitBreakdownFile = this._buildingUnitBreakdownFile$.getValue()
    _building.unitBreakdownFile = unitBreakdownFile.isUploaded 
      ? { name: unitBreakdownFile.file.name, path: unitBreakdownFile.file.path }
      : null
    _building.floorAreaMeasumentComment = this.floorAreaMeasurementCommentControl.value
    _building.floorAreaMeasurements = this._getFloorAreaMeasurementsValue()
    return _building;
  }

  onCoordinatePointsChange(points: CoordinatePoint[]) {
    this._points$.next(points)
  }
  onLandAreasChange(items: LandArea[]) {
    this._landAreas$.next(items)
  }

  onAddConstructionCategory() {
    this.constructionCategoryFormArray.push(new FormGroup({
      title: new FormControl(null) ,
      description: new FormControl(null)
    }))
    this._constructionCategoryIds.push(null)
  }
  onRemoveConstructionCategory(index: number) {
    this.constructionCategoryFormArray.removeAt(index)
  }
  private _setConstructionCategoryFormArray(items: {id: number, title: string, description: string}[]) {
    if (items.length === 0) {
      this.constructionCategoryFormArray.push(new FormGroup({
        title: new FormControl(null),
        description: new FormControl(null)
      }))
      this._constructionCategoryIds.push(null)
    } else {
      items.forEach(item => {
        this.constructionCategoryFormArray.push(new FormGroup({
          title: new FormControl(item.title),
          description: new FormControl(item.description)
        }))
        this._constructionCategoryIds.push(item.id)
      })
    }
  }
  private _getConstructionCategoryValue() {
    const vals: {id: number, title: string, description: string}[] = []
    this.constructionCategoryFormArray.controls.forEach((group, i) => {
      const _new = {id: null, title: null, description: null}
      if (this._constructionCategoryIds[i]) {
        _new.id = this._constructionCategoryIds[i]
      }
      _new.title = group.controls.title.value
      _new.description = group.controls.description.value
      vals.push(_new)
    })
    return vals;
  }

  onClickUploadAccommodationLayout() {
    this.layoutFileInputRef.nativeElement.click();
  }
  onUploadAccommodationLayout(event: any) {
    const files: FileList = event.target.files;
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })
    event.srcElement.value = null

    this.fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      this._buildingAccommodationLayoutFile$.next({
        isUploaded: true,
        file: {
          name: body.uploaded_files[0].name,
          path: body.uploaded_files[0].path,
          display_name: normalizeName(body.uploaded_files[0].name)
        }
      })
    })
  }
  onClickUploadUnitBreakdown() {
    this.unitBreakdownFileInputRef.nativeElement.click();
  }
  onUploadUnitBreakdown(event: any) {
    const files: FileList = event.target.files;
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })
    event.srcElement.value = null

    this.fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      this._buildingUnitBreakdownFile$.next({
        isUploaded: true,
        file: {
          name: body.uploaded_files[0].name,
          path: body.uploaded_files[0].path,
          display_name: normalizeName(body.uploaded_files[0].name)
        }
      })
    })
  }

  private _setFloorAreaMeasurementFormArray(items: {
    id: number,
    name: string,
    title: string,
    property_sub_type_id: number,
    measurement_standard_id: number,
    path: string
  }[]) {
    if (items.length === 0) {
      this.floorAreaMeasurementFormArray.push(new FormGroup({
        title: new FormControl(null),
        property_sub_type_id: new FormControl(null),
        measurement_standard_id: new FormControl(null),
        name: new FormControl(null),
        path: new FormControl(null),
        display_name: new FormControl(null)
      }))
      this._floorAreaMeasurementIds.push(null)
    } else {
      items.forEach(item => {
        this.floorAreaMeasurementFormArray.push(new FormGroup({
          title: new FormControl(item.title),
          property_sub_type_id: new FormControl(item.property_sub_type_id),
          measurement_standard_id: new FormControl(item.measurement_standard_id),
          name: new FormControl(item.name),
          path: new FormControl(item.path),
          display_name: new FormControl(normalizeName(item.name))
        }))
        this._floorAreaMeasurementIds.push(item.id)
      })
    }
  }
  onClickUploadFloorAreaMeasurement(index: number) {
    const inputElement = document.createElement('input');
    inputElement.type = 'file'
    inputElement.accept = '.csv'
    inputElement.multiple = false
    inputElement.click()

    inputElement.onchange = () => {
      this._uploadFloorAreaMeasurement(inputElement.files, index)
    }
  }
  private _uploadFloorAreaMeasurement(files: FileList, index: number) {
    const formData = new FormData()
    Array.from(files).forEach((file, i) => {
      formData.append('files[' + i + ']', file, file.name)
    })

    this.fileUploadService.uploadFile(formData).subscribe(value => {
      if (value.type !== HttpEventType.Response) {
        return;
      }
      const body = value.body as any
      const group = this.floorAreaMeasurementFormArray.at(index)
      group.controls.name.patchValue(body.uploaded_files[0].name)
      group.controls.path.patchValue(body.uploaded_files[0].path)
      group.controls.display_name.patchValue(normalizeName(body.uploaded_files[0].name))
      this.cdr.markForCheck()
    })
  }
  private _getPropertySubTypes() {
    this.propertySubTypeService.fetchListing().subscribe(response => {
      this._propertySubTypes$.next(response)
    })
  }
  private _getMeasurementStandards() {
    this.measurementStandardService.fetchListing().subscribe(response => {
      this._measurementStandards$.next(response)
    })
  }
  filterMeasurementStandards(index: number, standards: {id: number, name: string, property_sub_type_id: number}[]) {
    const group = this.floorAreaMeasurementFormArray.at(index)
    return standards.filter(item => item.property_sub_type_id === group.controls.property_sub_type_id.value || !item.property_sub_type_id)
  }
  onAddFloorAreaMeasurement() {
    this.floorAreaMeasurementFormArray.push(new FormGroup({
      title: new FormControl(null),
      property_sub_type_id: new FormControl(null),
      measurement_standard_id: new FormControl(null),
      name: new FormControl(null),
      path: new FormControl(null),
      display_name: new FormControl(null)
    }))
    this._floorAreaMeasurementIds.push(null)
  }
  onRemoveFloorAreaMeasurement(index: number) {
    this.floorAreaMeasurementFormArray.removeAt(index)
  }
  private _getFloorAreaMeasurementsValue() {
    const vals: {id: number, title: string, property_sub_type_id: number, measurement_standard_id: number, name: string, path: string}[] = []
    this.floorAreaMeasurementFormArray.controls.forEach((group, i) => {
      const _new = { id: null, title: null, property_sub_type_id: null, measurement_standard_id: null, name: null, path: null }
      if (this._floorAreaMeasurementIds[i]) {
        _new.id = this._floorAreaMeasurementIds[i]
      }
      _new.title = group.controls.title.value
      _new.property_sub_type_id = group.controls.property_sub_type_id.value
      _new.measurement_standard_id = group.controls.measurement_standard_id.value
      _new.name = group.controls.name.value
      _new.path = group.controls.path.value
      vals.push(_new)
    })
    return vals;
  }
}

function normalizeName(text: string): string {
  if (!text) return null
  return text.replace(/^\d+_/, "")
}